import React, { useState, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Card, Grid, Button, Icon, Input, Spacer } from '@components/common'
import { useStyletron } from 'styletron-react'
import {
  useFetchFieldParmsQuery,
  useGetFieldsQuery,
} from '@api/fieldParm/fieldParmEndpointsHooks'
import * as styles from './Styles'

interface AssetColumn {
  id: string
  name: string
}

// Asset field IDs that we need to get names for from API calls
const assetFieldIds: string[] = [
  'AssetCompanyID',
  'AssetID',
  'AS_DESC',
  'AS_DESC_E',
  'AS_SERIAL',
  'AS_BAR_ID',
  'AS_ADD_DAT',
  'AS_ADD_USR',
  'AS_CHG_DAT',
  'AS_CHG_USR',
  'AS_KEY0_ID',
  'AS_KEY1_ID',
  'AS_KEY2_ID',
  'AS_KEY3_ID',
  'AS_KEY4_ID',
  'AS_KEY5_ID',
  'AS_KEY6_ID',
  'AS_KEY7_ID',
  'AS_KEY8_ID',
  'AS_KEY9_ID',
  'AS_CAT_ID',
  'AS_TCLS_ID',
  'AS_PRI_UCD',
  'AS_SEC_UCD',
  'AS_TAXB_CD',
  'AS_TAXL_CD',
  'AS_TAX_BUS',
  'AS_TAX_PTP',
  'AS_REC_DAT',
  'AS_VEN_ID',
  'AS_VEN_NM',
  'AS_REC_PON',
  'AS_REC_USR',
  'AS_FIN_COS',
  'AS_FIN_SAL',
  'AS_FIN_VAL',
  'AS_FIN_CLC',
  'AS_FIN_CSC',
  'AS_FIN_COC',
  'AS_FIN_CFC',
  'AS_FIN_TAC',
  'AS_DIS_CD',
  'AS_DIS_DAT',
  'AS_DIS_SAL',
  'AS_DIS_NAM',
  'AS_DIS_SYD',
  'AS_IN_POL',
  'AS_REP_DAT',
  'AS_STAT_CD',
  'AS_BOK4_CD',
  'AS_SCLS_ID',
  'AS_4CLS_ID',
  'AS_WRO_CD',
  'AS_WRO_DAT',
  'AS_PAR_ASS',
  'AS_NSER_DT',
  'AS_MAIN_CN',
  'AS_DEP_STC',
  'AS_LEASE',
  'AS_LOCD_CD',
  'AS_LAPC_ID',
  'AS_LDPC_ID',
  'AS_SERD_CD',
  'AS_SAPC_ID',
  'AS_SDPC_ID',
  'AS_UC1_UCD',
  'AS_UC2_UCD',
  'AS_UC3_UCD',
  'DP_MET_CD',
  'DP_LIFETIM',
  'DP_STRT_CD',
  'DP_RATE',
  'DP_RATE_CD',
]

const AssetColumnsSelector: React.FC = () => {
  const [css] = useStyletron()
  const { setValue, watch } = useFormContext()
  const selectedColumns = watch('selectedColumns') || []
  const [searchTerm, setSearchTerm] = useState('')

  const { data: asFieldParms, isLoading: asLoading } = useFetchFieldParmsQuery({
    tablePrefix: 'AS',
    pageNumber: 1,
    itemsPerPage: 100,
    filter: {},
  })

  const { data: dpFieldParms, isLoading: dpLoading } = useFetchFieldParmsQuery({
    tablePrefix: 'DP',
    pageNumber: 1,
    itemsPerPage: 50,
    filter: {},
  })

  const { data: listFields, isLoading: listFieldsLoading } = useGetFieldsQuery()

  const availableColumns = useMemo(() => {
    if (!asFieldParms || !dpFieldParms || !listFields) {
      return []
    }

    const columns: AssetColumn[] = []

    const fieldNameMap = new Map<string, string>()

    asFieldParms.list.forEach(field => {
      fieldNameMap.set(field.PM_NAME, field.PM_PMT)
    })

    dpFieldParms.list.forEach(field => {
      fieldNameMap.set(field.PM_NAME, field.PM_PMT)
    })

    listFields.forEach(field => {
      fieldNameMap.set(field.Name, field.Value)
    })

    assetFieldIds.forEach(fieldId => {
      const fieldName = fieldNameMap.get(fieldId)
      if (fieldName) {
        columns.push({
          id: fieldId,
          name: fieldName,
        })
      }
    })

    return columns
  }, [asFieldParms, dpFieldParms, listFields])

  const filteredAvailableColumns = availableColumns.filter(
    column =>
      !selectedColumns.includes(column.id) &&
      column.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const selectedColumnsData = selectedColumns
    .map((id: string) => availableColumns.find(col => col.id === id))
    .filter(Boolean)

  const moveColumn = (columnId: string, direction: 'up' | 'down') => {
    const currentIndex = selectedColumns.indexOf(columnId)
    if (
      (direction === 'up' && currentIndex > 0) ||
      (direction === 'down' && currentIndex < selectedColumns.length - 1)
    ) {
      const newColumns = [...selectedColumns]
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      ;[newColumns[currentIndex], newColumns[targetIndex]] = [
        newColumns[targetIndex],
        newColumns[currentIndex],
      ]
      setValue('selectedColumns', newColumns)
    }
  }

  const addColumn = (columnId: string) => {
    setValue('selectedColumns', [...selectedColumns, columnId])
  }

  const removeColumn = (columnId: string) => {
    setValue(
      'selectedColumns',
      selectedColumns.filter((id: string) => id !== columnId)
    )
  }

  const addAllColumns = () => {
    setValue('selectedColumns', [
      ...selectedColumns,
      ...filteredAvailableColumns.map(col => col.id),
    ])
  }

  const removeAllColumns = () => {
    setValue('selectedColumns', [])
  }

  return (
    <Grid className={css(styles.gridContainer)}>
      <Grid.Item s={5}>
        <Card title="Available Columns">
          <div>
            <Input
              placeholder="Search columns..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Spacer y="compact" />
          </div>
          <div className={css(styles.scrollableContainer)}>
            {filteredAvailableColumns.map(column => (
              <div key={column.id} className={css(styles.columnItem)}>
                <div className={css(styles.columnInfo)}>
                  <div className={css(styles.columnName)}>{column.name}</div>
                </div>
                <Button
                  type="button"
                  onClick={() => addColumn(column.id)}
                  size="compact"
                  icon={<Icon name="Plus" size="s" />}
                >
                  Add
                </Button>
              </div>
            ))}
          </div>
          <div className={css(styles.bulkActionsContainer)}>
            <Button type="button" onClick={addAllColumns} size="compact">
              Add All
            </Button>
          </div>
        </Card>
      </Grid.Item>

      <Grid.Item s={2}>
        <div className={css(styles.transferButtonContainer)}>
          <Button
            type="button"
            onClick={addAllColumns}
            size="compact"
            className={css(styles.transferButton)}
          >
            <Icon name="ChevronRight" color="var(--primary-color)" size="s" />
          </Button>
          <Button
            type="button"
            onClick={removeAllColumns}
            size="compact"
            className={css(styles.transferButton)}
          >
            <Icon name="ChevronLeft" color="var(--primary-color)" size="s" />
          </Button>
        </div>
      </Grid.Item>

      <Grid.Item s={5}>
        <Card title="Selected Columns">
          <div className={css(styles.scrollableContainer2)}>
            {selectedColumnsData.map((column: AssetColumn, index: number) => (
              <div key={column.id} className={css(styles.columnItem)}>
                <div className={css(styles.columnInfo)}>
                  <div className={css(styles.columnName)}>{column.name}</div>
                </div>
                <div className={css(styles.selectedColumnActions)}>
                  <Button
                    type="button"
                    onClick={() => moveColumn(column.id, 'up')}
                    size="compact"
                    disabled={index === 0}
                    icon={<Icon name="ChevronUp" color="var(--primary-color)" size="s" />}
                    shape="square"
                  />
                  <Button
                    type="button"
                    onClick={() => moveColumn(column.id, 'down')}
                    size="compact"
                    disabled={index === selectedColumnsData.length - 1}
                    icon={<Icon name="ChevronDown" color="var(--primary-color)" size="s" />}
                    shape="square"
                  />
                  <Button
                    type="button"
                    onClick={() => removeColumn(column.id)}
                    size="compact"
                    icon={<Icon name="Trash2" color="var(--danger-color)" size="s" />}
                    shape="square"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className={css(styles.bulkActionsContainer)}>
            <Button type="button" onClick={removeAllColumns} size="compact">
              Remove All
            </Button>
          </div>
        </Card>
      </Grid.Item>
    </Grid>
  )
}

export default AssetColumnsSelector
